"use client";
import { useEffect, useRef } from "react";

interface ProductData {
  name: string;
  reviewCount: number;
  avgRating: number;
  customerCount: number;
  recentReviews: number;
}

interface ProductChartProps {
  data: ProductData[];
  type?: 'bar' | 'horizontal-bar';
  height?: number;
}

export function ProductChart({ data, type = 'horizontal-bar', height = 300 }: ProductChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data.length) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas and set background
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Set background color based on theme
    const isDarkMode = document.documentElement.classList.contains('dark');
    if (isDarkMode) {
      ctx.fillStyle = '#111827'; // dark:bg-gray-900
      ctx.fillRect(0, 0, rect.width, rect.height);
    } else {
      ctx.fillStyle = '#ffffff'; // bg-white
      ctx.fillRect(0, 0, rect.width, rect.height);
    }

    // Chart dimensions
    const padding = { top: 20, right: 40, bottom: 60, left: 120 };
    const chartWidth = rect.width - padding.left - padding.right;
    const chartHeight = rect.height - padding.top - padding.bottom;

    // Get top 8 products for display
    const topProducts = data.slice(0, 8);
    const maxValue = Math.max(...topProducts.map(d => d.reviewCount));

    // Colors for monochrome theme
    const barColor = isDarkMode ? '#E5E7EB' : '#374151'; // gray-200 : gray-700
    const textColor = isDarkMode ? '#9CA3AF' : '#6B7280'; // gray-400 : gray-500
    const labelColor = isDarkMode ? '#F3F4F6' : '#1F2937'; // gray-100 : gray-800

    if (type === 'horizontal-bar') {
      // Draw horizontal bars
      const barHeight = chartHeight / topProducts.length * 0.7;
      const barSpacing = chartHeight / topProducts.length;

      topProducts.forEach((product, i) => {
        const y = padding.top + i * barSpacing + (barSpacing - barHeight) / 2;
        const barWidth = (product.reviewCount / maxValue) * chartWidth;

        // Draw bar
        ctx.fillStyle = barColor;
        ctx.fillRect(padding.left, y, barWidth, barHeight);

        // Draw product name (left side)
        ctx.fillStyle = labelColor;
        ctx.font = "12px sans-serif";
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        const truncatedName = product.name.length > 15 ? product.name.substring(0, 15) + '...' : product.name;
        ctx.fillText(truncatedName, padding.left - 10, y + barHeight / 2);

        // Draw value (right side of bar)
        ctx.fillStyle = textColor;
        ctx.font = "11px sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(product.reviewCount.toString(), padding.left + barWidth + 5, y + barHeight / 2);

        // Draw rating if available
        if (product.avgRating > 0) {
          ctx.fillStyle = isDarkMode ? '#9CA3AF' : '#6B7280'; // monochrome rating
          ctx.font = "10px sans-serif";
          ctx.fillText(`★${product.avgRating}`, padding.left + barWidth + 35, y + barHeight / 2);
        }
      });
    } else {
      // Vertical bar chart
      const barWidth = chartWidth / topProducts.length * 0.7;
      const barSpacing = chartWidth / topProducts.length;

      topProducts.forEach((product, i) => {
        const x = padding.left + i * barSpacing + (barSpacing - barWidth) / 2;
        const barHeight = (product.reviewCount / maxValue) * chartHeight;
        const y = padding.top + chartHeight - barHeight;

        // Draw bar
        ctx.fillStyle = barColor;
        ctx.fillRect(x, y, barWidth, barHeight);

        // Draw value on top of bar
        ctx.fillStyle = textColor;
        ctx.font = "11px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(product.reviewCount.toString(), x + barWidth / 2, y - 5);

        // Draw product name (bottom)
        ctx.fillStyle = labelColor;
        ctx.font = "10px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        const truncatedName = product.name.length > 10 ? product.name.substring(0, 10) + '...' : product.name;
        ctx.fillText(truncatedName, x + barWidth / 2, rect.height - padding.bottom + 10);
      });
    }

    // Draw title
    ctx.fillStyle = labelColor;
    ctx.font = "bold 14px sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("Product Review Volume", padding.left, 5);

  }, [data, type, height]);

  if (!data.length) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <div className="text-lg font-medium mb-2">No Product Data</div>
          <div className="text-sm">Product analytics will appear here once you have reviews</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      <div className="flex-1 min-h-[300px] w-full">
        <canvas
          ref={canvasRef}
          className="h-full w-full"
          style={{ width: '100%', height: `${height}px` }}
        />
      </div>
    </div>
  );
}
